import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { store } from '../../stores/reduxStore'
import { Card, CardHeader, CardBody, Row, Col, Table, Badge, FormGroup, Label, Input, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import { Bar } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'

const PROJECTS_QUERY = gql`
  query CompletionForecastProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query CompletionForecastPerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const FORECAST_QUERY = gql`
  query StatsCompletionForecast($project_id: ID!) {
    stats_completion_forecast(project_id: $project_id) {
      project_id
      project_name
      planned_end_date
      welding {
        total_mm complete_mm progress_pct avg_weekly_mm
        weeks_to_complete estimated_end_date days_diff trend
        weekly_data { week mm }
      }
      inspections {
        type_id type_name is_ri total_mm complete_mm progress_pct avg_weekly_mm
        weeks_to_complete estimated_end_date days_diff trend
        weekly_data { week mm }
      }
    }
  }
`

const trendBadge = (trend) => {
  const map = { accelerating: ['success', 'fa-arrow-up'], decelerating: ['danger', 'fa-arrow-down'], stable: ['secondary', 'fa-minus'] }
  const [color, icon] = map[trend] || ['secondary', 'fa-minus']
  return <Badge color={color}><i className={`fa ${icon} mr-1`} />{trend}</Badge>
}

const diffBadge = (days) => {
  if (days === null || days === undefined) return <span className="text-muted">—</span>
  if (days <= 0) return <Badge color="success"><i className="fa fa-check mr-1" />{Math.abs(days)}d ahead</Badge>
  return <Badge color="danger"><i className="fa fa-warning mr-1" />{days}d late</Badge>
}

const pctBar = (pct) => {
  const color = pct >= 80 ? '#28a745' : pct >= 40 ? '#ffc107' : '#dc3545'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div className="progress flex-grow-1" style={{ height: '16px', minWidth: '80px' }}>
        <div className="progress-bar" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}>
          {pct}%
        </div>
      </div>
    </div>
  )
}

const RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 180 days' },
]

const filterWeeklyData = (weeklyData, days) => {
  const cutoff = new Date(Date.now() - days * 86400000)
  return (weeklyData || []).filter(d => new Date(d.week) >= cutoff)
}

const ForecastBlock = ({ data, label, plannedEnd, rangeDays }) => {
  if (!data) return null
  return (
    <div>
      <Row className="mb-4">
        {[
          ['Progress', pctBar(data.progress_pct)],
          ['Complete', `${(data.complete_mm || 0).toLocaleString()} mm`],
          ['Total required', `${(data.total_mm || 0).toLocaleString()} mm`],
          ['Avg weekly pace', data.avg_weekly_mm ? `${data.avg_weekly_mm.toLocaleString()} mm` : '—'],
          ['Weeks to complete', data.weeks_to_complete !== null && data.weeks_to_complete !== undefined ? data.weeks_to_complete : '—'],
          ['Est. end date', data.estimated_end_date || '—'],
        ].map(([lbl, val]) => (
          <Col md="2" key={lbl} className="mb-2">
            <div className="text-center p-2 border rounded h-100">
              <div className="text-muted" style={{ fontSize: '11px' }}>{lbl}</div>
              <div className="mt-1"><strong>{val}</strong></div>
            </div>
          </Col>
        ))}
      </Row>

      <Table bordered responsive size="sm" className="mb-4">
        <thead>
          <tr>
            <th>Estimated end date</th>
            <th>Planned end date</th>
            <th>Difference</th>
            <th>Pace trend</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>{data.estimated_end_date || '—'}</strong></td>
            <td>{plannedEnd || '—'}</td>
            <td>{diffBadge(data.days_diff)}</td>
            <td>{trendBadge(data.trend)}</td>
          </tr>
        </tbody>
      </Table>

      {data.weekly_data && data.weekly_data.length > 0 && (
        <div>
          <h6 className="text-muted">{label} — weekly pace [mm], last {rangeDays} days</h6>
          <Bar
            data={{
              labels: filterWeeklyData(data.weekly_data, rangeDays).map(d => d.week),
              datasets: [
                {
                  label: 'mm',
                  data: filterWeeklyData(data.weekly_data, rangeDays).map(d => d.mm),
                  backgroundColor: 'rgba(54,162,235,0.6)',
                  borderColor: 'rgba(54,162,235,1)',
                  borderWidth: 1
                },
                data.avg_weekly_mm ? {
                  label: 'Avg',
                  data: data.weekly_data.map(() => data.avg_weekly_mm),
                  type: 'line',
                  borderColor: 'rgba(255,99,132,0.8)',
                  borderDash: [5, 5],
                  fill: false,
                  pointRadius: 0
                } : null
              ].filter(Boolean)
            }}
            options={{
              maintainAspectRatio: true,
              legend: { display: true },
              scales: { yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: 'mm' } }] }
            }}
          />
        </div>
      )}
    </div>
  )
}

const mergeInspections = (inspections, planned_end_date) => {
  if (!inspections || !inspections.length) return null
  const total_mm = inspections.reduce((s, i) => s + (i.total_mm || 0), 0)
  const complete_mm = inspections.reduce((s, i) => s + (i.complete_mm || 0), 0)
  const progress_pct = total_mm > 0 ? Math.round(complete_mm / total_mm * 100) : 0
  const avg_weekly_mm = inspections.reduce((s, i) => s + (i.avg_weekly_mm || 0), 0)
  const weeks_to_complete = avg_weekly_mm > 0 ? Math.round((total_mm - complete_mm) / avg_weekly_mm) : null
  const validDates = inspections.map(i => i.estimated_end_date).filter(Boolean)
  const estimated_end_date = validDates.length ? validDates.sort().reverse()[0] : null
  let days_diff = null
  if (estimated_end_date && planned_end_date) {
    const diff = Math.round((new Date(estimated_end_date) - new Date(planned_end_date)) / 86400000)
    days_diff = diff
  }
  const trends = inspections.map(i => i.trend).filter(Boolean)
  const trend = trends.includes('decelerating') ? 'decelerating' : trends.includes('accelerating') ? 'accelerating' : 'stable'
  const weekMap = {}
  inspections.forEach(i => (i.weekly_data || []).forEach(w => {
    weekMap[w.week] = (weekMap[w.week] || 0) + (w.mm || 0)
  }))
  const weekly_data = Object.keys(weekMap).sort().map(week => ({ week, mm: weekMap[week] }))
  return { total_mm, complete_mm, progress_pct, avg_weekly_mm, weeks_to_complete,
    estimated_end_date, days_diff, trend, weekly_data }
}

const AllTab = ({ forecast }) => {
  if (!forecast) return null
  const { welding, inspections, planned_end_date } = forecast
  const allRows = [
    { label: 'Welding', data: welding },
    ...(inspections || []).map(i => ({ label: i.type_name, data: i }))
  ]

  return (
    <div>
      <Table bordered striped responsive size="sm">
        <thead>
          <tr>
            <th>Type</th>
            <th>Progress</th>
            <th>Complete mm</th>
            <th>Total mm</th>
            <th>Avg weekly mm</th>
            <th>Weeks left</th>
            <th>Est. end date</th>
            <th>Diff vs planned</th>
            <th>Trend</th>
          </tr>
        </thead>
        <tbody>
          {allRows.map(({ label, data }) => data ? (
            <tr key={label}>
              <td><strong>{label}</strong></td>
              <td style={{ minWidth: '120px' }}>{pctBar(data.progress_pct)}</td>
              <td>{(data.complete_mm || 0).toLocaleString()}</td>
              <td>{(data.total_mm || 0).toLocaleString()}</td>
              <td>{data.avg_weekly_mm ? data.avg_weekly_mm.toLocaleString() : '—'}</td>
              <td>{data.weeks_to_complete !== null && data.weeks_to_complete !== undefined ? data.weeks_to_complete : '—'}</td>
              <td>{data.estimated_end_date || '—'}</td>
              <td>{diffBadge(data.days_diff)}</td>
              <td>{trendBadge(data.trend)}</td>
            </tr>
          ) : null)}
        </tbody>
      </Table>
      <div className="text-muted small mt-1">Planned end date: <strong>{planned_end_date || '—'}</strong></div>
      <div className="mt-3 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
        <strong>Column descriptions:</strong>
        <ul className="mb-0 mt-1 pl-3">
          <li><strong>Progress</strong> — Complete mm / Total mm × 100%</li>
          <li><strong>Complete mm</strong> — total length of completed welds / inspections [mm]</li>
          <li><strong>Total mm</strong> — total required length based on weld lengths and inspection category ranges [mm]</li>
          <li><strong>Avg weekly mm</strong> — average weekly pace over the last {8} weeks [mm/week]; calculated from actual logs grouped by week</li>
          <li><strong>Weeks left</strong> — <em>(Total mm − Complete mm) / Avg weekly mm</em></li>
          <li><strong>Est. end date</strong> — today + Weeks left × 7 days</li>
          <li><strong>Diff vs planned</strong> — Est. end date − Planned end date; negative = ahead of schedule, positive = delayed</li>
          <li><strong>Trend</strong> — pace comparison between first and second half of the {8}-week window: &gt;10% faster = accelerating, &lt;−10% = decelerating, otherwise stable</li>
        </ul>
        <div className="mt-2 text-muted">
          <strong>Note:</strong> Avg weekly mm, Weeks left, Est. end date and Trend are always based on the last {8} weeks regardless of the chart range selector (visible on detail tabs).
        </div>
      </div>
    </div>
  )
}

class CompletionForecast extends Component {
  constructor(props) {
    super(props)
    this.state = {
      projects: [],
      userCompanyIds: [],
      selectedProjectId: null,
      forecast: null,
      loading: false,
      projectsLoading: true,
      error: null,
      activeTab: 'all',
      chartRange: 90
    }
  }

  componentDidMount() {
    const personId = store.getState().user.data.person_id
    const params = new URLSearchParams(this.props.location && this.props.location.search)
    const urlProjectId = params.get('project_id')
    Promise.all([
      this.props.client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      this.props.client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([projectsRes, personRes]) => {
      this.setState({
        projects: projectsRes.data.projects.list,
        userCompanyIds: (personRes.data.person.company_ids || []).map(String),
        projectsLoading: false
      }, () => { if (urlProjectId) this.handleProjectChange(urlProjectId) })
    }).catch(e => this.setState({ error: e.message, projectsLoading: false }))
  }

  handleProjectChange(projectId) {
    if (!projectId) {
      this.setState({ selectedProjectId: null, forecast: null })
      this.props.history && this.props.history.replace('/reports/completion-forecast')
      return
    }
    this.setState({ selectedProjectId: projectId, forecast: null })
    this.props.history && this.props.history.replace(`/reports/completion-forecast?project_id=${projectId}`)
    this.fetchForecast(projectId)
  }

  fetchForecast(projectId) {
    if (!projectId) return
    this.setState({ loading: true, error: null })
    this.props.client.query({
      query: FORECAST_QUERY,
      fetchPolicy: 'network-only',
      variables: { project_id: projectId }
    }).then(({ data }) => {
      this.setState({ forecast: data.stats_completion_forecast, loading: false })
    }).catch(e => this.setState({ error: e.message, loading: false }))
  }

  render() {
    const { projects, userCompanyIds, selectedProjectId, forecast, loading, projectsLoading, error, activeTab, chartRange } = this.state
    const filteredProjects = projects.filter(p => {
      if (!userCompanyIds.length) return true
      try {
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      } catch (e) { return false }
    })
    const allInspections = forecast && forecast.inspections ? forecast.inspections : []
    const riInspection = allInspections.find(i => i.is_ri)
    const ndtInspections = allInspections.filter(i => !i.is_ri)
    const ndtMerged = mergeInspections(ndtInspections, forecast && forecast.planned_end_date)

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-calendar-check-o mr-2" />Completion Forecast</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            {(loading || projectsLoading) && (
            <div className="spinner-item" id="loader-bg">
              <RiseLoader color="#20a8d8" loading={true} />
            </div>
          )}

            <Row className="mb-3">
              <Col sm={4}>
                <FormGroup>
                  <Label>Project</Label>
                  <Input type="select" value={selectedProjectId || ''}
                    onChange={e => this.handleProjectChange(e.target.value || null)}>
                    <option value="">— select project —</option>
                    {filteredProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              {activeTab !== 'all' && (
              <Col sm={3}>
                <FormGroup>
                  <Label>Chart range</Label>
                  <Input type="select" value={chartRange} onChange={e => this.setState({ chartRange: parseInt(e.target.value) })}>
                    {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              )}

            </Row>

            {error && <div className="text-danger mb-3">Error: {error}</div>}

            {!selectedProjectId && !loading && (
              <div className="text-center text-muted py-5">Select a project to view the forecast</div>
            )}

            {forecast && !loading && (
              <div>
                <Nav tabs className="mb-3">
                  {[['all', 'All'], ['welding', 'Welding']].map(([id, label]) => (
                    <NavItem key={id}>
                      <NavLink className={activeTab === id ? 'active' : ''} style={{ cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={() => this.setState({ activeTab: id })}>
                        {label}
                      </NavLink>
                    </NavItem>
                  ))}
                  {riInspection && (
                    <NavItem>
                      <NavLink className={activeTab === 'ri' ? 'active' : ''} style={{ cursor: 'pointer' }}
                        onClick={() => this.setState({ activeTab: 'ri' })}>
                        RI
                      </NavLink>
                    </NavItem>
                  )}
                  {ndtMerged && (
                    <NavItem>
                      <NavLink className={activeTab === 'ndt' ? 'active' : ''} style={{ cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={() => this.setState({ activeTab: 'ndt' })}>
                        NDT
                      </NavLink>
                    </NavItem>
                  )}
                  {ndtInspections.map(insp => (
                    <NavItem key={insp.type_id}>
                      <NavLink className={activeTab === `insp_${insp.type_id}` ? 'active' : ''} style={{ cursor: 'pointer' }}
                        onClick={() => this.setState({ activeTab: `insp_${insp.type_id}` })}>
                        {insp.type_name}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="all">
                    <AllTab forecast={forecast} />
                  </TabPane>
                  <TabPane tabId="welding">
                    <ForecastBlock data={forecast.welding} label="Welding" plannedEnd={forecast.planned_end_date} rangeDays={chartRange} />
                  </TabPane>
                  {riInspection && (
                    <TabPane tabId="ri">
                      <ForecastBlock data={riInspection} label="RI" plannedEnd={forecast.planned_end_date} rangeDays={chartRange} />
                    </TabPane>
                  )}
                  {ndtMerged && (
                    <TabPane tabId="ndt">
                      <ForecastBlock data={ndtMerged} label="NDT" plannedEnd={forecast.planned_end_date} rangeDays={chartRange} />
                    </TabPane>
                  )}
                  {ndtInspections.map(insp => (
                    <TabPane key={insp.type_id} tabId={`insp_${insp.type_id}`}>
                      <ForecastBlock data={insp} label={insp.type_name} plannedEnd={forecast.planned_end_date} rangeDays={chartRange} />
                    </TabPane>
                  ))}
                </TabContent>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(CompletionForecast)



// WEBPACK FOOTER //
// ./src/components/Report/CompletionForecast.js